/**
 * Users & Roles and My Profile rules (Traci's review, 23 Sep 2026; prototype v108 §12, §13). Pure.
 *
 *   Admin (Traci, Matt): edit any member's name, email and role; deactivate and reactivate; invite, resend, delete
 *   invitations. Everyone: change their own name, email and password; never their own role.
 *   Guards: nobody deactivates or demotes themselves, and the last active admin cannot be removed.
 */
export const TEAM_ROLES = [
  { key: 'admin', label: 'Admin' },
  { key: 'sales', label: 'Sales' },
  { key: 'billing', label: 'Billing' },
  { key: 'editing', label: 'Editing' },
  { key: 'talent_coordinator', label: 'Talent' }
]
const ROLE_KEYS = new Set(TEAM_ROLES.map(r => r.key))
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/**
 * @param member   { id, name, email, role, active }
 * @param edit     { name?, email?, role?, active? }
 * @param ctx      { actorId, activeAdmins: number of active admins now }
 * @returns { ok, errors, changes: { name?, email?, role?, active? } }
 */
export function planMemberEdit(member, edit = {}, { actorId, activeAdmins = 0 } = {}) {
  const errors = []
  const changes = {}
  if (edit.name !== undefined) {
    const n = String(edit.name).trim()
    if (!n) errors.push('A name is required')
    else if (n !== (member.name || '')) changes.name = n.slice(0, 120)
  }
  if (edit.email !== undefined) {
    const e = String(edit.email).trim().toLowerCase()
    if (!EMAIL.test(e)) errors.push('That email address does not look right')
    else if (e !== String(member.email || '').toLowerCase()) changes.email = e
  }
  if (edit.role !== undefined) {
    if (!ROLE_KEYS.has(edit.role)) errors.push('Unknown role')
    else if (edit.role !== member.role) changes.role = edit.role
  }
  if (edit.active !== undefined && Boolean(edit.active) !== Boolean(member.active)) changes.active = Boolean(edit.active)

  const self = actorId && actorId === member.id
  const losesAdmin = member.role === 'admin' && member.active && (changes.active === false || (changes.role && changes.role !== 'admin'))
  if (self && changes.active === false) errors.push('You cannot deactivate your own account')
  if (self && changes.role) errors.push('Your role is changed by another admin')
  if (losesAdmin && activeAdmins <= 1) errors.push('At least one active admin must remain')
  return { ok: errors.length === 0, errors, changes }
}

export function planInvite({ email, role, name } = {}) {
  const errors = []
  const e = String(email || '').trim().toLowerCase()
  if (!EMAIL.test(e)) errors.push('Email is required')
  if (!ROLE_KEYS.has(role)) errors.push('Pick a role')
  return { ok: errors.length === 0, errors, invite: { email: e, role, name: String(name || '').trim().slice(0, 120) || null } }
}

/**
 * Add Member with an email that already has a login (Traci, 24 Sep 2026: "if someone's email was used before, it can't
 * be used again"). A login that already holds a team role is on the list already: edit it there. Any other login
 * (a customer or talent account, or a past team member whose role was removed) gets the role attached, so the
 * person signs in with the account they have instead of being stuck.
 * @param roles  the account's current user_roles values
 * @returns 'already_member' | 'attach'
 */
export function existingAccountAction(roles = []) {
  return roles.some(r => ROLE_KEYS.has(r)) ? 'already_member' : 'attach'
}

/** "Pending", "Expired" or "Accepted" for an invitation row. */
export function inviteStatus(inv, now = new Date()) {
  if (inv?.used) return 'Accepted'
  return new Date(inv?.expires_at || 0) < now ? 'Expired' : 'Pending'
}

/** Password rule for My Profile: at least 10 characters, not all one kind. */
export function passwordProblems(pw, confirm) {
  const p = String(pw || '')
  const out = []
  if (p.length < 10) out.push('At least 10 characters')
  if (!/[A-Za-z]/.test(p) || !/[0-9]/.test(p)) out.push('Use letters and at least one number')
  if (confirm !== undefined && p !== confirm) out.push('The two passwords do not match')
  return out
}
